import React from 'react'
import { Cascader, Space, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { contextRequest } from '../../../redux/context/actions'
import { updateCompanyRequest } from '../../../redux/context/company/actions'
import {
  contextCompanyCountrySelector,
  contextCompanyFormSelector,
  contextCompanySelector
} from '../../../redux/context/company/selectors'
import { CompanyForm, Country } from '../../../types/company/Company'
import { AppDispatch } from '../../../redux/store'

interface CountryOption {
  value: Country
  label: React.ReactNode
  children: CompanyFormOption[]
}

interface CompanyFormOption {
  value: CompanyForm
  label: React.ReactNode
}

/**
 * Cascader for selecting company country and form.
 * Updates the company settings when changed.
 */
interface CompanyFormCascaderProps {
  disabled?: boolean
}

const CompanyFormCascader: React.FC<CompanyFormCascaderProps> = ({ disabled }) => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useDispatch()

  const company = useSelector(contextCompanySelector)
  const country = useSelector(contextCompanyCountrySelector)
  const companyForm = useSelector(contextCompanyFormSelector)

  const handleChange = (values?: (string | number)[]) => {
    if (!values || !company) return
    const [newCountry, newCompanyForm] = values as [Country, CompanyForm]
    dispatch(
      contextRequest(updateCompanyRequest, {
        ...company,
        country: newCountry,
        form: newCompanyForm
      })
    )
  }

  const options: CountryOption[] = [
    {
      value: Country.fi,
      label: t(`country:${Country.fi}`),
      children: [
        { value: CompanyForm.limited, label: t(`accountMapPage:${CompanyForm.limited}`) },
        { value: CompanyForm.association, label: t(`accountMapPage:${CompanyForm.association}`) },
        { value: CompanyForm.realEstate, label: t(`accountMapPage:${CompanyForm.realEstate}`) }
      ]
    },
    {
      value: Country.se,
      label: t(`country:${Country.se}`),
      children: [{ value: CompanyForm.limited, label: t(`accountMapPage:${CompanyForm.limited}`) }]
    }
  ]

  return (
    <Space>
      <Typography.Text>{t('global:company-form')}:</Typography.Text>
      <Cascader
        allowClear={false}
        value={[country, companyForm] as any}
        options={options}
        onChange={handleChange}
        style={{ minWidth: 180 }}
        disabled={disabled}
      />
    </Space>
  )
}

export default CompanyFormCascader
